import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useColorScheme,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { RootState } from '../../../redux/Store';
import { ApplicationActions, RobotActions, slotNumberFromSlotName } from '../../../utils/constants';
import { readSwerveSlotPythonSource } from '../../../utils/readSwerveSlotPythonSource';
import { getSaveSlotDisplayProgramName, isSaveSlotEmpty } from '../../../utils/saveSlotProjectId';
import { createSwerveSlotBackupProject } from '../../../utils/swerveSlotBackupProject';
import checkProjectName from '../../../utils/validation/checkProjectName';
import type { Project } from '../../Projects/projects';
import SwerveSlotList from '../../Drawers/SwerveSlotList';
import MainProgramIcon from '../../Icons/Program/main-program.svg?react';
import SwerveIcon from '../../Icons/Robots/swerve_fill_icon.svg?react';

interface LoadFromSwerveDialogProps {
  open: boolean;
  onClose: () => void;
}

const LoadFromSwerveDialog = ({ onClose }: LoadFromSwerveDialogProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const { mode, systemMode } = useColorScheme();
  const colorMode = mode === 'system' ? systemMode : mode;
  const robotState = useSelector((state: RootState) => state.robot.robotState);
  const projects = useSelector((state: RootState) => state.project.projects);
  const isConnected = robotState === RobotActions.STATE.CONNECTED;
  const [loadingSlotIndex, setLoadingSlotIndex] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleOpenConnectionDrawer = () => {
    dispatch(applicationSlice.actions.onOpenDialog({ dialog: null, options: {} }));
    dispatch(projectSlice.actions.onOpenDrawer(ApplicationActions.Drawer.CONNECTION_DRAWER));
  };

  const getDisplayName = (programTitle: string, projectId?: string) => {
    if (!programTitle?.trim()) return null;
    return checkProjectName(programTitle.trim(), projectId || '');
  };

  const handleLoadSlot = async (
    slotKey: string,
    programTitle: string | undefined,
    bpsProjectId: string | undefined,
    bpsProjectLastModifiedUtcMs: number | undefined
  ) => {
    if (loadingSlotIndex !== null) return;
    setLoadingSlotIndex(slotKey);
    setLoadError(null);
    try {
      const pythonSource = await readSwerveSlotPythonSource(slotKey);
      const baseProject = await createSwerveSlotBackupProject({
        pythonSource,
        slot: { programTitle, bpsProjectId, bpsProjectLastModifiedUtcMs },
        fallbackName: slotKey,
      });
      const dedupedName = checkProjectName(baseProject.name, baseProject.id || '');
      const existingProject = baseProject.id ? projects.find((p: Project) => p.id === baseProject.id) : undefined;
      const project: Project = existingProject ? { ...existingProject, ...baseProject, name: dedupedName } : { ...baseProject, name: dedupedName };

      if (existingProject) {
        dispatch(projectSlice.actions.onReplaceProject(project));
      } else {
        dispatch(projectSlice.actions.onAddProject(project));
      }
      dispatch(projectSlice.actions.onLoadProject(project));
      dispatch(projectSlice.actions.setSelectedSaveSlot(slotNumberFromSlotName(slotKey)));
      dispatch(applicationSlice.actions.onOpenDialog({ dialog: null, options: {} }));
      window.projectId = project.id;
      onClose();
      navigate(`/editor/${project.id}`);
    } catch (error) {
      console.error('Failed to load project from Drive Unit slot', error);
      setLoadError(error instanceof Error ? error.message : String(error));
    } finally {
      setLoadingSlotIndex(null);
    }
  };

  return (
    <>
      <DialogTitle variant="sectionTitle" align="center">
        <Trans i18nKey="loadFromSwerveDialog.title">Load from Swerve Drive Robot</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: '24px' }}>
        {!isConnected ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <SwerveIcon
                style={{
                  width: 64,
                  height: 64,
                  color: colorMode === 'dark' ? theme.vars?.palette?.common?.white : theme.vars?.palette?.primary?.main,
                }}
              />
            </Box>
            <Typography variant="body1" paragraph>
              <Trans i18nKey="loadFromSwerveDialog.connectPrompt">
                Connect to your Swerve Drive Robot to load a project. The connection panel has been opened on the right.
              </Trans>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Trans i18nKey="loadFromSwerveDialog.connectHint">After connecting, return here to select a project from a slot.</Trans>
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button variant="contained" onClick={handleOpenConnectionDrawer}>
                <Trans i18nKey="loadFromSwerveDialog.connectButton">Connect to Robot</Trans>
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <Trans i18nKey="loadFromSwerveDialog.selectSlot">Select a slot to load its project:</Trans>
            </Typography>
            {loadError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }} role="alert">
                <Trans i18nKey="loadFromSwerveDialog.loadError">Could not load project from Drive Unit. Please try again.</Trans>
              </Typography>
            )}
            <SwerveSlotList
              renderSlot={(slot, slotKey) => {
                const hasProject = !isSaveSlotEmpty(slot);
                const displayName = hasProject ? getDisplayName(getSaveSlotDisplayProgramName(slot?.programTitle), slot?.bpsProjectId) : null;
                const isRowLoading = loadingSlotIndex === slotKey;
                const isAnotherRowLoading = loadingSlotIndex !== null && loadingSlotIndex !== slotKey;
                const slotNumber = slotNumberFromSlotName(slotKey);
                const slotSecondaryLabel = t('slot.selectedSlot', 'Slot {{slotNumber}}', { slotNumber: String(slotNumber) });
                return (
                  <ListItemButton
                    disabled={!hasProject || isAnotherRowLoading}
                    onClick={() => {
                      if (hasProject) {
                        void handleLoadSlot(slotKey, slot?.programTitle, slot?.bpsProjectId, slot?.bpsProjectLastModifiedUtcMs);
                      }
                    }}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {isRowLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Box
                          component={MainProgramIcon}
                          aria-hidden
                          sx={{
                            width: 22,
                            height: 22,
                            display: 'block',
                            color: hasProject ? 'text.primary' : 'text.disabled',
                            flexShrink: 0,
                            opacity: hasProject ? 1 : 0.5,
                          }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={hasProject ? displayName : <Trans i18nKey="loadFromSwerveDialog.emptySlot">Empty Slot</Trans>}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: hasProject ? 'text.primary' : 'text.disabled',
                        fontStyle: hasProject ? undefined : 'italic',
                      }}
                      secondary={hasProject ? slotSecondaryLabel : undefined}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                );
              }}
            />
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'center', padding: 2 }}>
        <Button variant="text" onClick={onClose} disabled={loadingSlotIndex !== null}>
          <Trans i18nKey="button.cancel">Cancel</Trans>
        </Button>
      </DialogActions>
    </>
  );
};

export default LoadFromSwerveDialog;
